import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/app';
import { setPayload } from '../../../redux/reducer/payload';
import { makeBidAskSelector } from '../../../redux/selector';
import UseStopLossTakeProfit from '../../../utilities/useStopLossTakeProfit';
import { cn, DirectionIcons } from '@vt/vt-ui';

const StopLoss = memo(({ handlePrice }) => {
  const payload = useAppSelector((state) => state.payload?.payload);
  const dispatch = useAppDispatch();
  const getBidAsk = useMemo(makeBidAskSelector, []);
  const bid = useAppSelector((state) => getBidAsk(state, 'bid'));
  const slref = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const instrumentInfo = useAppSelector((state) => state.symbol.instrumentInfo);

  const digit = instrumentInfo?.digit ?? 0;

  useEffect(() => {
    if (slref.current) {
      if (typeof payload.price_sl === 'string') {
        slref.current.value = payload.price_sl;
      } else {
        slref.current.value = payload.price_sl <= 0 ? "" : payload.price_sl;
      }
    }
  }, [payload.price_sl]);

  return (
    <div className={cn("relative border hover:border-[#0FEF9E] rounded-lg mt-2", isFocused ? "border-[#0FEF9E]" : "border-[#4B4B4B]")}>
      <input
        placeholder="0000.00"
        type="number"
        ref={slref}
        onChange={({ target: { value } }) => {
          dispatch(
            setPayload({
              ...payload,
              price_sl: value
            })
          );
        }}
        defaultValue={payload.price_sl}
        className="input-left-lotsize p-[10px] text-[#e5e5e5] !w-[calc(100%-24px)]"
        style={{ caretColor: isFocused ? '#0FEF9E' : 'auto' }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseDown={() => {
          if (payload.price_sl === 0 || payload.price_sl === '') {
            dispatch(
              setPayload({
                ...payload,
                price_sl: bid
              })
            );
          }
        }}
      />
      <div className="absolute right-0 top-0 bottom-0 flex w-6 flex-col rounded-r-lg border-l border-[#4b4b4b] hover:border-[#0FEF9E]">
        <button
          type="button"
          className={cn(
            'vt-flex group hover:bg-[#0FEF9E] flex-1 items-center justify-center rounded-tr-md border-b border-[#4b4b4b] transition-colors',
          )}
          onClick={() => handlePrice('inc', payload.price_sl, 'price_sl', digit, dispatch, payload, bid)}
        >
          <DirectionIcons.Up className="h-4 w-4 stroke-neutral-400 fill-neutral-400 group-hover:stroke-[#151515] group-hover:fill-[#151515] transition-colors" />
        </button>
        <button
          type="button"
          className={cn(
            'vt-flex group flex-1 hover:bg-[#0FEF9E] hover:border-[#0FEF9E] items-center justify-center rounded-br-md transition-colors',
          )}
          onClick={() => handlePrice('dec', payload.price_sl, 'price_sl', digit, dispatch, payload, bid)}
        >
          <DirectionIcons.Down className="h-4 w-4 stroke-neutral-400 fill-neutral-400 group-hover:stroke-[#151515] group-hover:fill-[#151515] transition-colors" />
        </button>
      </div>
    </div>
  )
});

StopLoss.displayName = 'StopLoss';

const TakeProfit = memo(({ handlePrice }) => {
  const payload = useAppSelector((state) => state.payload?.payload);
  const dispatch = useAppDispatch();
  const getBidAsk = useMemo(makeBidAskSelector, []);
  const bid = useAppSelector((state) => getBidAsk(state, 'bid'));
  const tpRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const instrumentInfo = useAppSelector((state) => state.symbol.instrumentInfo);

  const digit = instrumentInfo?.digit ?? 0;

  useEffect(() => {
    if (tpRef.current) {
      if (typeof payload.price_tp === 'string') {
        tpRef.current.value = payload.price_tp;
      } else {
        tpRef.current.value = payload.price_tp <= 0 ? "" : payload.price_tp;
      }
    }
  }, [payload.price_tp]);

  return (
    <div className={cn("relative border hover:border-[#0FEF9E] rounded-lg mt-2", isFocused ? "border-[#0FEF9E]" : "border-[#4B4B4B]")}>
      <input
        placeholder="0000.00"
        type="number"
        ref={tpRef}
        onChange={({ target: { value } }) => {
          dispatch(
            setPayload({
              ...payload,
              price_tp: value
            })
          );
        }}
        defaultValue={payload.price_tp}
        className="input-left-lotsize p-[10px] text-[#e5e5e5] !w-[calc(100%-24px)]"
        style={{ caretColor: isFocused ? '#0FEF9E' : 'auto' }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseDown={() => {
          if (payload.price_tp === 0 || payload.price_tp === '') {
            dispatch(
              setPayload({
                ...payload,
                price_tp: bid ?? 0
              })
            );
          }
        }}
      />
      <div className="absolute right-0 top-0 bottom-0 flex w-6 flex-col rounded-r-lg border-l border-[#4b4b4b] hover:border-[#0FEF9E]">
        <button
          type="button"
          className={cn(
            'vt-flex group hover:bg-[#0FEF9E] flex-1 items-center justify-center rounded-tr-md border-b border-[#4b4b4b] transition-colors',
          )}
          onClick={() => handlePrice('inc', payload.price_tp, 'price_tp', digit, dispatch, payload, bid)}
        >
          <DirectionIcons.Up className="h-4 w-4 stroke-neutral-400 fill-neutral-400 group-hover:stroke-[#151515] group-hover:fill-[#151515] transition-colors" />
        </button>
        <button
          type="button"
          className={cn(
            'vt-flex group flex-1 hover:bg-[#0FEF9E] hover:border-[#0FEF9E] items-center justify-center rounded-br-md transition-colors',
          )}
          onClick={() => handlePrice('dec', payload.price_tp, 'price_tp', digit, dispatch, payload, bid)}
        >
          <DirectionIcons.Down className="h-4 w-4 stroke-neutral-400 fill-neutral-400 group-hover:stroke-[#151515] group-hover:fill-[#151515] transition-colors" />
        </button>
      </div>
    </div>
  )
});

TakeProfit.displayName = 'TakeProfit';

const StopLossAndTakeProfit = memo(() => {
  const { handlePrice } = UseStopLossTakeProfit();

  return (
    <div className="flex gap-4">
      <div className="mt-4 flex-1">
        <p className="mb-2 text-sm text-[#e5e5e5]">Stop Loss</p>
        <StopLoss handlePrice={handlePrice} />
      </div>
      <div className="mt-4 flex-1">
        <p className="mb-2 text-sm text-[#e5e5e5]">Take Profit</p>
        <TakeProfit handlePrice={handlePrice} />
      </div>
    </div>
  );
});

StopLossAndTakeProfit.displayName = 'StopLossAndTakeProfit';

export default StopLossAndTakeProfit;
